import React from "react";
import { evaluateWarnings } from "./warningEngine";
import type { ResolvedWarning } from "./types";
// JSON artifacts
import promptTemplates from "./promptTemplates.json";
import variableRegistry from "./variableRegistry.json";
// Prefer aggregated TS index over single JSON for scalability
import aggregatedFieldWarningRules from "./fieldRules";
import {
  BUDGET_FIELD_MAPPINGS,
  getApiFieldNameFromSchemaField,
} from "@/shared/lib/budgetFieldMapping";
import { calculateAge } from "@/shared/lib/utils/others";
import { useTriggers } from "./TriggersContext";
import { useCustomerMetadata } from "./CustomerMetadataContext";
import { useForm as useZustandForm } from "@/shared/state/store";

type UseFieldWarningsOpts = {
  fieldName?: string | undefined;
  categoryKey?: string | undefined;
  formValues?: Record<string, any>;
  profile?: Record<string, any>;
  triggers?: Record<string, any>;
  sectionOuterOverride?: string; // income | assets | fixed-expenses | flexible-expenses | debts
  max?: number;
  /**
   * Optional key used purely to invalidate memoized warning results.
   * When this value changes, the hook recomputes warnings even if
   * form/profile/trigger references are unchanged.
   */
  refreshKey?: number;
};

function toUpperSnake(apiFieldName: string): string {
  return apiFieldName
    .replace(/([A-Z])/g, "_$1")
    .toUpperCase()
    .replace(/^_/, "");
}

function outerFromCategory(cat?: string): string {
  if (!cat) return "income";
  const incomeCats = ["salary", "benefits", "pension", "otherSources"];
  const assetCats = ["properties", "otherAssets"];
  const fixedCats = [
    "household",
    "utilities",
    "care",
    "transport",
    "school",
    // Fixed-expense pension section is mapped from route "pension-insurance" -> category "pensionInsurance"
    // (see `updateBudgetPage.tsx`). Support both legacy kebab-case and current camelCase.
    "pensionInsurance",
    "pension-insurance",
    "professionals",
    "otherSourcesFixed",
  ];
  const flexibleCats = ["leisure", "housekeeping", "personalCosts", "savings"];
  const debtCats = ["debtsAdministration", "debts"];
  if (incomeCats.includes(cat)) return "income";
  if (assetCats.includes(cat)) return "assets";
  if (fixedCats.includes(cat)) return "fixed-expenses";
  if (flexibleCats.includes(cat)) return "flexible-expenses";
  if (debtCats.includes(cat)) return "debts";
  return "income";
}

function isBlankishForHydration(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (typeof value === "string" && value.trim() === "")
  );
}

function coerceEnumString(value: unknown): string | undefined {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const rec = value as Record<string, unknown>;
  const candidate =
    (typeof rec.value === "string" && rec.value) ||
    (typeof rec.label === "string" && rec.label) ||
    (typeof rec.name === "string" && rec.name) ||
    undefined;
  if (typeof candidate !== "string") return undefined;
  const trimmed = candidate.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function getValueAtPath(obj: unknown, path: string): unknown {
  if (!path) return obj;
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    const rec = obj as Record<string, unknown>;
    if (Object.prototype.hasOwnProperty.call(rec, path)) return rec[path];
  }
  const parts = path.split(".").filter(Boolean);
  let acc: unknown = obj;
  for (let i = 0; i < parts.length; i += 1) {
    if (acc == null) return undefined;
    if (acc && typeof acc === "object" && !Array.isArray(acc)) {
      const rec = acc as Record<string, unknown>;
      const remaining = parts.slice(i).join(".");
      if (Object.prototype.hasOwnProperty.call(rec, remaining)) return rec[remaining];
      acc = rec[parts[i] as string];
      continue;
    }
    return undefined;
  }
  return acc;
}

function countDigitsBeforeDecimal(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const rawFull = typeof value === "string" ? value : String(value ?? "");
  const dotIdx = rawFull.indexOf(".");
  const raw = dotIdx >= 0 ? rawFull.slice(0, dotIdx) : rawFull;
  const matches = raw.match(/\d/g);
  return matches ? matches.length : 0;
}

function resolveVarIdForDigits(
  upper: string,
  variablesMap: Record<string, unknown>,
): string | null {
  // Preferred: exact match to the normalised upper-snake API field id
  if (Object.prototype.hasOwnProperty.call(variablesMap, upper)) {
    return upper;
  }

  // Some legacy ids include an underscore before trailing digits, e.g.
  // OTHER_SALARY_WAGES1 -> OTHER_SALARY_WAGES_1
  const withUnderscoreBeforeDigits = upper.replace(/(\D)(\d+)/g, "$1_$2");
  if (
    withUnderscoreBeforeDigits !== upper &&
    Object.prototype.hasOwnProperty.call(variablesMap, withUnderscoreBeforeDigits)
  ) {
    return withUnderscoreBeforeDigits;
  }

  // Some legacy ids omit underscores entirely, e.g.
  // TV_LICENCE -> TVLICENCE
  const withoutUnderscores = upper.replace(/_/g, "");
  if (
    withoutUnderscores !== upper &&
    Object.prototype.hasOwnProperty.call(variablesMap, withoutUnderscores)
  ) {
    return withoutUnderscores;
  }

  return null;
}

function findFirstNonBlankInFormStore(
  formData: Record<string, any> | undefined,
  path: string,
): unknown {
  if (!formData || typeof formData !== "object") return undefined;
  for (const entry of Object.values(formData)) {
    if (!entry || typeof entry !== "object") continue;
    const v = getValueAtPath(entry, path);
    if (!isBlankishForHydration(v)) return v;
  }
  return undefined;
}

function hydrateCrossSectionBudgetValues(
  fv: Record<string, any>,
  formStoreData: Record<string, any> | undefined,
): Record<string, any> {
  // Cross-section prompts (e.g. Housing Benefit vs Rent) need access to values that
  // may live in other forms. Hydrate missing values from the global form store.
  const out: Record<string, any> = { ...(fv || {}) };

  const hasHb =
    getValueAtPath(out, "housingBenefitLocalAllowance") !== undefined ||
    getValueAtPath(out, "housingBenefitLocalAllowance.amount") !== undefined ||
    getValueAtPath(out, "housingBenefitLocalAllowanceMode") !== undefined ||
    getValueAtPath(out, "housingBenefitLocalAllowance.frequency") !== undefined;

  if (!hasHb) {
    // Prefer concrete amount/frequency fields so empty objects don't block hydration.
    const hbAmt = findFirstNonBlankInFormStore(
      formStoreData,
      "housingBenefitLocalAllowance.amount",
    );
    const hbFreq = findFirstNonBlankInFormStore(
      formStoreData,
      "housingBenefitLocalAllowance.frequency",
    );
    const hbMode = findFirstNonBlankInFormStore(
      formStoreData,
      "housingBenefitLocalAllowanceMode",
    );
    const hbRaw = findFirstNonBlankInFormStore(
      formStoreData,
      "housingBenefitLocalAllowance",
    );

    if (!isBlankishForHydration(hbAmt) || !isBlankishForHydration(hbFreq)) {
      out.housingBenefitLocalAllowance = { amount: hbAmt, frequency: hbFreq };
    } else if (!isBlankishForHydration(hbRaw)) {
      // Support legacy shape where HB is stored as a direct number/string
      out.housingBenefitLocalAllowance = hbRaw;
    }
    if (!isBlankishForHydration(hbMode)) {
      out.housingBenefitLocalAllowanceMode = hbMode;
    }
  }

  const hasRent =
    getValueAtPath(out, "houseRent") !== undefined ||
    getValueAtPath(out, "houseRent.amount") !== undefined ||
    getValueAtPath(out, "houseRentMode") !== undefined ||
    getValueAtPath(out, "houseRent.frequency") !== undefined;

  if (!hasRent) {
    const rentAmt = findFirstNonBlankInFormStore(formStoreData, "houseRent.amount");
    const rentFreq = findFirstNonBlankInFormStore(formStoreData, "houseRent.frequency");
    const rentMode = findFirstNonBlankInFormStore(formStoreData, "houseRentMode");
    const rentRaw = findFirstNonBlankInFormStore(formStoreData, "houseRent");

    if (!isBlankishForHydration(rentAmt) || !isBlankishForHydration(rentFreq)) {
      out.houseRent = { amount: rentAmt, frequency: rentFreq };
    } else if (!isBlankishForHydration(rentRaw)) {
      out.houseRent = rentRaw;
    }
    if (!isBlankishForHydration(rentMode)) {
      out.houseRentMode = rentMode;
    }
  }

  return out;
}

export function useFieldWarnings(
  opts: UseFieldWarningsOpts
): ResolvedWarning[] {
  const {
    fieldName,
    categoryKey,
    formValues,
    profile,
    triggers,
    sectionOuterOverride,
    max,
    refreshKey,
  } = opts;
  const triggersFromContext = useTriggers();
  const customerMetadata = useCustomerMetadata();
  const { formData: formStoreData } = useZustandForm();

  return React.useMemo(() => {
    try {


      if (!fieldName || !categoryKey) {
        if (fieldName === "propertiesMortgageOutstanding") {
   
        }
        return [];
      }

      let effectiveCategory = categoryKey;
      const sectionOuter =
        sectionOuterOverride || outerFromCategory(effectiveCategory);
      // Resolve schema field -> api field for rule matching
      let apiField = getApiFieldNameFromSchemaField(
        fieldName,
        effectiveCategory as any
      );

      // Debug logging for takeHomeSalary field
      if (fieldName === "pensionPay") {

      }
      // Fallback: scan all categories to find mapping by schema field
      if (!apiField) {
        const categories = Object.keys(BUDGET_FIELD_MAPPINGS) as Array<
          keyof typeof BUDGET_FIELD_MAPPINGS
        >;
        for (const cat of categories) {
          const mapping = (BUDGET_FIELD_MAPPINGS as any)[cat];
          const fieldMapping = mapping?.[fieldName];
          if (
            fieldMapping &&
            typeof fieldMapping === "object" &&
            fieldMapping.amount
          ) {
            apiField = fieldMapping.amount;
            effectiveCategory = cat as any;
            break;
          }
        }
        if (!apiField) return [];
      }

      // Best-effort profile snapshot: start from provided profile, then localStorage,
      // then merge metadata + live form values as fallbacks
      let effectiveProfile: Record<string, any> = profile || {};
      if (!profile && typeof window !== "undefined") {
        try {
          const storedCustomerRaw = localStorage.getItem("customerData");
          const storedCustomer = storedCustomerRaw
            ? JSON.parse(storedCustomerRaw)
            : null;
    
          if (storedCustomer && typeof storedCustomer === "object") {
            effectiveProfile = {
              customerEmploymentStatus: coerceEnumString(
                (storedCustomer as any).customerEmploymentStatus
              ),
              housingTenure:
                coerceEnumString(
                  (storedCustomer as any).housingTenure ||
                    (storedCustomer as any).accomodationType ||
                    (storedCustomer as any).accommodationType
                ),
              dependentChildrenUnder16: storedCustomer.dependentChildrenUnder16,
              dependentChildrenAged16Plus:
                storedCustomer.dependentChildrenAged16Plus ??
                storedCustomer.dependentchildrenaged16plus,
              universalCredit:
                storedCustomer.otherIncludeUniversalCredit ??
                storedCustomer.universalCredit,
              noOfVehiclesInHouseholds:
                storedCustomer.noOfVehiclesInHouseholds ?? 0,
              debtor1DateOfBirthStr:
                storedCustomer.debtor1DateOfBirthStr ??
                storedCustomer.dateOfBirth ??
                storedCustomer.debtor1DateOfBirth,
              partnerEmploymentStatus: coerceEnumString(
                (storedCustomer as any).partnerEmploymentStatus
              ),
            };
          }
        } catch {}
      }

      // Overlay any metadata coming from the React state provider so that
      // profile/onboarding dialogs immediately influence the warning engine.
      if (!profile && customerMetadata) {
        effectiveProfile = {
          ...effectiveProfile,
          customerEmploymentStatus:
            coerceEnumString(customerMetadata.customerEmploymentStatus) ??
            coerceEnumString(effectiveProfile.customerEmploymentStatus) ??
            effectiveProfile.customerEmploymentStatus,
          partnerEmploymentStatus:
            coerceEnumString(customerMetadata.partnerEmploymentStatus) ??
            coerceEnumString(effectiveProfile.partnerEmploymentStatus) ??
            effectiveProfile.partnerEmploymentStatus,
          housingTenure:
            coerceEnumString(customerMetadata.accommodationType) ??
            coerceEnumString(effectiveProfile.housingTenure) ??
            effectiveProfile.housingTenure,
          dependentChildrenUnder16:
            customerMetadata.dependentChildrenUnder16 ??
            effectiveProfile.dependentChildrenUnder16,
          dependentChildrenAged16Plus:
            customerMetadata.dependentChildrenAged16Plus ??
            effectiveProfile.dependentChildrenAged16Plus,
        };
      }

      // Merge live form fallbacks (values seen in your logs)
      const fvRaw = (formValues || {}) as Record<string, any>;
      const fv = hydrateCrossSectionBudgetValues(fvRaw, formStoreData);
      if (
        !effectiveProfile.customerEmploymentStatus &&
        fv.customerEmploymentStatus
      ) {
        effectiveProfile.customerEmploymentStatus =
          coerceEnumString(fv.customerEmploymentStatus) ??
          fv.customerEmploymentStatus;
      }
      if (!effectiveProfile.housingTenure && fv.accommodationType) {
        effectiveProfile.housingTenure =
          coerceEnumString(fv.accommodationType) ?? fv.accommodationType;
      }
      if (
        effectiveProfile.dependentChildrenUnder16 == null &&
        fv.dependentChildrenUnder16 != null
      ) {
        effectiveProfile.dependentChildrenUnder16 = fv.dependentChildrenUnder16;
      }
      if (
        effectiveProfile.dependentChildrenAged16Plus == null &&
        fv.dependentChildrenAged16Plus != null
      ) {
        effectiveProfile.dependentChildrenAged16Plus =
          fv.dependentChildrenAged16Plus;
      }

      // Vehicle details: if the toggle is "No", the counter field may be hidden/unregistered.
      // In that case, ensure warnings do not keep a stale stored count (e.g. 1) around.
      const hvRaw = fv.haveVehicle;
      const hasVehicle =
        typeof hvRaw === "boolean"
          ? hvRaw
          : typeof hvRaw === "string"
            ? hvRaw.trim().toLowerCase() === "y"
            : undefined;
      if (hasVehicle === false) {
        effectiveProfile.noOfVehiclesInHouseholds = 0;
      }
      if (
        fv.noOfVehiclesInHouseholds != null
      ) {
        // Always prefer live form state for warnings so toggling "No" immediately drives the
        // vehicle count to 0 (instead of lingering localStorage/profile values triggering warnings).
        const v = fv.noOfVehiclesInHouseholds;
        const coerced =
          typeof v === "number"
            ? v
            : typeof v === "string"
              ? parseInt(v, 10)
              : undefined;
        if (typeof coerced === "number" && !Number.isNaN(coerced)) {
          effectiveProfile.noOfVehiclesInHouseholds = coerced;
        }
      }
      if (effectiveProfile.universalCredit == null) {
        const uc = fv.otherIncludeUniversalCredit ?? fv.universalCredit;
        if (uc != null) {
          effectiveProfile.universalCredit =
            typeof uc === "number" ? uc > 0 : Boolean(uc);
        }
      }
      if (
        !effectiveProfile.partnerEmploymentStatus &&
        fv.partnerEmploymentStatus
      ) {
        effectiveProfile.partnerEmploymentStatus =
          coerceEnumString(fv.partnerEmploymentStatus) ??
          fv.partnerEmploymentStatus;
      }

      // Derived profile flags used by warning rules.
      // NOTE: Some warning rules reference `profile.isPensionAge` (boolean). Historically we
      // computed this in page-level code, but `QuestionGroup` doesn't pass a profile snapshot
      // into this hook, so we derive it here as a best-effort fallback.
      if (effectiveProfile.isPensionAge == null) {
        const PENSION_AGE_YEARS = 65;
        const dobCandidate =
          effectiveProfile.debtor1DateOfBirthStr ??
          effectiveProfile.dateOfBirth ??
          effectiveProfile.debtor1DateOfBirth ??
          fv.debtor1DateOfBirthStr ??
          fv.dateOfBirth ??
          fv.debtor1DateOfBirth;
        const ageYears = calculateAge(dobCandidate);
        if (typeof ageYears === "number") {
          effectiveProfile.isPensionAge = ageYears >= PENSION_AGE_YEARS;
        }
      }

      const effectiveIncome: Record<string, any> = {};

      // Helper: read amount from form values supporting both nested objects
      // (e.g. fv.universalCredit.amount on updateBudgetPage) and flattened
      // dot-key strings (e.g. fv["universalCredit.amount"] on budgetPage
      // breakdown dialog where SpecialScreen flattens initialData).
      const readAmount = (schemaField: string): number => {
        // Try nested object first
        const nested = fv[schemaField];
        if (nested && typeof nested === "object" && nested.amount != null) {
          const n = typeof nested.amount === "number" ? nested.amount : parseFloat(String(nested.amount));
          return Number.isFinite(n) ? n : 0;
        }
        // Try flattened dot-key
        const flat = fv[`${schemaField}.amount`];
        if (flat != null) {
          const n = typeof flat === "number" ? flat : parseFloat(String(flat));
          return Number.isFinite(n) ? n : 0;
        }
        // Try plain number (non-question-group field)
        if (typeof nested === "number") return Number.isFinite(nested) ? nested : 0;
        if (typeof nested === "string") {
          const n = parseFloat(nested);
          return Number.isFinite(n) ? n : 0;
        }
        return 0;
      };

      effectiveIncome.universalCredit = readAmount("universalCredit");
      effectiveIncome.basicIncome = readAmount("basicIncome");
      
      // Income fields used in "depends_on_universal_credit" warning rules
      // Note: schema field names differ from API field names
      effectiveIncome.empSupportAllowanceStatutory = readAmount("empsupportallowancestatutory");
      effectiveIncome.jobseekerAllowanceContribute = readAmount("jobSeekerAllowanceContribute");

      // For profile‑driven warnings (like employment status), several rules
      // look at BOTH `profile.*` and `liveValues.*`. After profile/onboarding
      // dialogs, the profile snapshot is updated immediately, but the form
      // values that originally seeded those fields can remain stale, which
      // keeps the rule condition true even though the profile is now valid.
      //
      // To avoid this, we ensure the liveValues view for key profile fields
      // is always aligned with the effective profile snapshot.
      const liveValuesForWarnings: Record<string, any> = {
        ...fv,
        customerEmploymentStatus:
          coerceEnumString(effectiveProfile.customerEmploymentStatus) ??
          coerceEnumString(fv.customerEmploymentStatus) ??
          effectiveProfile.customerEmploymentStatus ??
          fv.customerEmploymentStatus,
        partnerEmploymentStatus:
          coerceEnumString(effectiveProfile.partnerEmploymentStatus) ??
          coerceEnumString(fv.partnerEmploymentStatus) ??
          effectiveProfile.partnerEmploymentStatus ??
          fv.partnerEmploymentStatus,
      };

      const triggersObj = triggers || triggersFromContext || {};
      const context = {
        profile: effectiveProfile,
        income: effectiveIncome,
        expense: {},
        assets: {},
        debts: {},
        triggers: triggersObj,
        section: { outer: sectionOuter, inner: categoryKey },
        schema: undefined,
        liveValues: liveValuesForWarnings,
      } as any;

      // Debug logging for pension warnings (narrow + safe)
      // NOTE: Keep logs scoped to one field to avoid noisy consoles in prod.
      if (fieldName === "pensionPay") {
        try {
          // eslint-disable-next-line no-console
          console.log("[WARN-DEBUG][PENSION] pre-eval", {
            fieldName,
            categoryKey,
            sectionOuter,
            mappedApiField: apiField,
            profileEmploymentStatus: effectiveProfile.customerEmploymentStatus,
            liveEmploymentStatus: liveValuesForWarnings.customerEmploymentStatus,
          });
        } catch {
          // ignore
        }
      }

      // Debug logging for OTHER_SALARY_WAGES_1
      if (
        apiField === "pensionPay" ||
        toUpperSnake(apiField) === "OTHER_SALARY_WAGES_1"
      ) {

      }

      const templatesMap = Object.fromEntries(
        (promptTemplates as any[]).map((t: any) => [t.id, t])
      );
      const variablesMap = Object.fromEntries(
        (variableRegistry as any[]).map((v: any) => [v.id, v])
      );

      let warnings = evaluateWarnings(
        context,
        templatesMap,
        variablesMap,
        aggregatedFieldWarningRules as any[],
        { max: max ?? 3 }
      );

      if (fieldName === "pensionPay") {
        try {
          const upperForDebug = apiField ? toUpperSnake(apiField) : "";
          // eslint-disable-next-line no-console
          console.log("[WARN-DEBUG][PENSION] post-eval", {
            mappedApiField: apiField,
            upperForDebug,
            totalWarningsFromEngine: warnings.length,
            warningIds: warnings.slice(0, 8).map((w) => w.id),
            warningFieldNames: warnings.slice(0, 8).map((w) => w.fieldName),
          });
        } catch {
          // ignore
        }
      }

      // Debug logging for MORTGAGE_OUTSTANDING warnings BEFORE filtering
      if (
        apiField === "pensionPay" ||
        toUpperSnake(apiField) === "MORTGAGE_OUTSTANDING"
      ) {

      }

      // Filter warnings for this api field (rule entries encode fieldName in UPPER_SNAKE)
      const upper = toUpperSnake(apiField);

      warnings = warnings.filter((w) => {
        const matches =
          (w.fieldName == "TVLICENCE"
            ? "TV_LICENCE"
            : w.fieldName || ""
          ).toUpperCase() === upper;
        if (
          !matches &&
          (apiField === "mortgageOutstanding" ||
            upper === "MORTGAGE_OUTSTANDING")
        ) {
     
        }
        return matches;
      });

      // Debug logging for BASIC_INCOME warnings AFTER filtering
      if (apiField === "pensionPay" || upper === "MORTGAGE_OUTSTANDING") {
  
      }

      // Additional guard: for "based_on_profile_to_enter", only show when the field is effectively empty
      const fieldObj = (formValues || {})[fieldName as string];
      const candidateValue =
        fieldObj && typeof fieldObj === "object"
          ? ((fieldObj as any).amount ?? fieldObj)
          : fieldObj;
      const normalizedValue =
        typeof candidateValue === "string"
          ? candidateValue.trim()
          : candidateValue;
      const isNumericString =
        typeof normalizedValue === "string" &&
        normalizedValue !== "" &&
        !Number.isNaN(Number(normalizedValue));
      const numericValue = isNumericString
        ? Number(normalizedValue)
        : normalizedValue;
      const isZeroLike = numericValue === 0;
      const isEmpty =
        normalizedValue === undefined ||
        normalizedValue === null ||
        (typeof normalizedValue === "string" && normalizedValue === "") ||
        isZeroLike;

      let filtered = warnings.filter((w) => {
        if (w.templateId === "based_on_profile_to_enter") {
          return isEmpty;
        }
        if (w.templateId === "based_on_profile_to_remove") {
          return !isEmpty;
        }
        if (w.templateId === "limit_prompt_without_values") {
          return !isEmpty;
        }
        return true;
      });

      if (fieldName === "pensionPay") {
        try {
          const upperForDebug = apiField ? toUpperSnake(apiField) : "";
          // eslint-disable-next-line no-console
          console.log("[WARN-DEBUG][PENSION] post-filter", {
            mappedApiField: apiField,
            upperForDebug,
            amountCandidate: candidateValue,
            normalizedValue,
            isEmpty,
            filteredCount: filtered.length,
            filteredTemplates: filtered.map((w) => w.templateId),
            filteredText: filtered.map((w) => w.resolvedText),
          });
        } catch {
          // ignore
        }
      }

      // Prioritize limit warnings over profile-based warnings
      filtered.sort((a, b) => {
        const limitTemplates = [
          "limit_prompt_without_values",
          "limit_prompt_with_values",
        ];
        const aIsLimit = limitTemplates.includes(a.templateId);
        const bIsLimit = limitTemplates.includes(b.templateId);
        if (aIsLimit && !bIsLimit) return -1;
        if (!aIsLimit && bIsLimit) return 1;
        return 0;
      });

      // Global digit-count warning (lowest priority):
      // Only show when NO other prompt is shown for this field.
      // Trigger when user typed more than 6 digits BEFORE the decimal point.
      if (filtered.length === 0) {
        const variablesMapRec = variablesMap as Record<string, unknown>;
        const resolvedVarId = resolveVarIdForDigits(upper, variablesMapRec);

        // QA requirement: never show the 6-digit review prompt for debt admin fee
        if (resolvedVarId === "DEBT_ADMIN_FEE") {
          return filtered;
        }

        const varSpec = resolvedVarId ? (variablesMapRec as any)[resolvedVarId] : undefined;
        if (resolvedVarId && varSpec?.valueType === "currency") {
          const digits = countDigitsBeforeDecimal(candidateValue);
          if (digits > 6) {
            const digitWarnings = evaluateWarnings(
              context,
              templatesMap,
              variablesMap,
              [
                {
                  fieldName: upper,
                  category: sectionOuter,
                  rules: [
                    {
                      templateId: "digit_count_review_amount",
                      when: true,
                      // Use resolved var id so token metadata resolves even for legacy ids
                      tokens: [{ role: "field", varId: resolvedVarId }],
                      severity: "warning",
                      level: "field",
                    },
                  ],
                },
              ] as any[],
              { max: 1 }
            );
            if (digitWarnings.length > 0) {
              return digitWarnings;
            }
          }
        }
      }

      // Debug logging for BASIC_INCOME final result
      if (apiField === "pensionPay" || upper === "MORTGAGE_OUTSTANDING") {
   
      }

      return filtered;
    } catch (e) {
      console.warn("useFieldWarnings error", e);
      return [];
    }
  }, [
    fieldName,
    categoryKey,
    formValues,
    profile,
    triggers,
    sectionOuterOverride,
    max,
    triggersFromContext,
    refreshKey,
    customerMetadata,
    formStoreData,
  ]);
}
